import { MedplumClient } from "@medplum/core";
import { DocumentReference } from "@medplum/fhirtypes";

import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import {
  getCodeableConceptCoding,
  getOrganizationReference,
  joinReferenceString,
} from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { fetchAllPaginatedResources } from "@/utils/pagination";

import { FileData } from "../types/export";

export const FILES_EXPORT_PAGE_SIZE = 20;
const PAGE_SIZE = FILES_EXPORT_PAGE_SIZE;

/** Content types supported for PDF merge (export). Filter applied client-side so listing matches Files tab. */
const SUPPORTED_CONTENT_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
]);

function isSupportedForExport(doc: DocumentReference): boolean {
  const coding = getCodeableConceptCoding(doc.type);
  if (coding?.system === Systems.LOINC && coding?.code === "34895-3") return false;
  const contentType = doc.content?.[0]?.attachment?.contentType;
  if (!contentType) return false;
  return SUPPORTED_CONTENT_TYPES.has(contentType);
}

export function getFilesQueryForExport(clientId: string, practiceId?: string) {
  return {
    patient: joinReferenceString("Patient", clientId),
    category: `${QuiltedHealthCodeSystems.UploadedDocumentSource}|manual`,
    ...(practiceId ? { _compartment: getOrganizationReference(practiceId) } : {}),
  };
}

/**
 * Fetches all DocumentReferences for the client (matching Files tab query), filters
 * client-side to supported types and non–edu-resources, and returns the full list.
 * Used so pagination is based on filtered count (no empty pages).
 */
export async function fetchAllFilesForExportAsData(
  medplum: MedplumClient,
  clientId: string,
  practiceId?: string,
): Promise<FileData[]> {
  const result = await fetchAllPaginatedResources<DocumentReference>(medplum, "DocumentReference", {
    ...getFilesQueryForExport(clientId, practiceId),
    _sort: "-_lastUpdated",
  });

  if (!result.success) {
    showSystemErrorNotification();
    return [];
  }

  const filtered = result.data.filter((doc) => isSupportedForExport(doc));
  return filtered.map((documentReference) => ({ documentReference }));
}

export async function fetchAllFilesForExport(
  medplum: MedplumClient,
  clientId: string,
  practiceId?: string,
) {
  const data = await fetchAllFilesForExportAsData(medplum, clientId, practiceId);
  return data
    .map((fileData) => fileData.documentReference.id)
    .filter((id): id is string => Boolean(id));
}

/**
 * Returns one page of supported files. Pagination is based on the filtered list
 * (fetches all, filters, then slices) so page count and items stay in sync.
 */
export async function fetchFilesForExport(
  medplum: MedplumClient,
  clientId: string,
  page: number,
  practiceId?: string,
) {
  const all = await fetchAllFilesForExportAsData(medplum, clientId, practiceId);
  const start = (page - 1) * PAGE_SIZE;
  const data = all.slice(start, start + PAGE_SIZE);
  const total = Math.ceil(all.length / PAGE_SIZE);

  return {
    data,
    total,
  };
}
